import moment from 'moment-jalaali';
import React, { useState, useEffect } from 'react';
import { MenuItem as MuiMenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Box, Grid, Switch,
  FormControlLabel, Avatar, Chip, Divider, useTheme, useMediaQuery, Card, CardContent
} from '@mui/material';
import { 
  Edit, Delete, Add, CloudUpload, PhotoCamera, RateReview, 
  NotificationsActive, Close, Star, LocationOn, Store
} from '@mui/icons-material';
import welfareService from '../../utils/welfareService';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';
import AIRewriteTextField from '../../components/AIRewriteTextField';

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' }
  }
};

const WelfareServicesManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    subCategoryId: '',
    image: '',
    gallery: [],
    isActive: true,
    isFeatured: false,
    sendNotification: false,
    order: 0,
    link: '',
    metadata: { provider: '', location: '', duration: '', durationJalali: '' }
  });

  // Helper function for date display
  moment.loadPersian({ usePersianDigits: false });

  const formatJalaliDate = (gDate) => {
    if (!gDate) return '';
    const parsed = moment(gDate);
    if (!parsed.isValid()) return '';
    return parsed.format('jYYYY/jMM/jDD');
  };

  const parseJalaliToGregorian = (value) => {
    if (!value) return '';
    const normalized = value.replace(/[^0-9/-]/g, '').replace(/-/g, '/');
    const candidates = ['jYYYY/jMM/jDD', 'jYYYY/jM/jD', 'YYYY/MM/DD', 'YYYY-MM-DD'];
    for (const format of candidates) {
      const parsed = moment(normalized, format, true);
      if (parsed.isValid()) {
        return parsed.format('YYYY-MM-DD');
      }
    }
    return '';
  };
  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchServices();
  }, [filterCategory, filterSubCategory]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await welfareService.getServices(filterCategory || null, null, filterSubCategory || null);
      setServices(data || []);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'خطا در بارگذاری خدمات';
      snackbar(msg);
      setServices([]);
    } finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const data = await welfareService.getCategories(true);
      setCategories(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpen = (svc = null) => {
    if (svc) {
      setCurrent(svc);
      setFormData({
        title: svc.title || '',
        description: svc.description || '',
        categoryId: svc.categoryId?._id || svc.categoryId || '',
        subCategoryId: svc.subCategoryId || '',
        image: svc.image || '',
        gallery: svc.gallery || [],
        isActive: svc.isActive !== undefined ? svc.isActive : true,
        isFeatured: svc.isFeatured || false,
        sendNotification: false,
        order: svc.order || 0,
        link: svc.link || '',
        metadata: {
          provider: svc.metadata?.provider || '',
          location: svc.metadata?.location || '',
          duration: svc.metadata?.duration || '',
          durationJalali: formatJalaliDate(svc.metadata?.duration || '')
        }
      });
    } else {
      setCurrent(null);
      setFormData({
        title: '', description: '', categoryId: '', subCategoryId: '', image: '', gallery: [], isActive: true,
        isFeatured: false, sendNotification: false, order: 0, link: '', metadata: { provider: '', location: '', duration: '', durationJalali: '' }
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMetadataChange = (field, value) => {
    if (field === 'durationJalali') {
      // Convert Jalali to Gregorian and store both
      const gregorian = parseJalaliToGregorian(value);
      setFormData({
        ...formData,
        metadata: {
          ...formData.metadata,
          durationJalali: value,
          duration: gregorian
        }
      });
    } else {
      setFormData({ ...formData, metadata: { ...formData.metadata, [field]: value } });
    }
  };

  const handleImageUpload = async (file, field = 'image', galleryIndex = null) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/welfare/upload-image', fd);
      const filename = res.data.filename;
      if (field === 'image') setFormData({ ...formData, image: filename });
      else if (field === 'gallery') {
        const gallery = [...formData.gallery];
        if (galleryIndex === null) gallery.push(filename); else gallery[galleryIndex] = filename;
        setFormData({ ...formData, gallery });
      }
      successMessage('تصویر با موفقیت بارگذاری شد');
    } catch (err) {
      snackbar('خطا در بارگذاری تصویر');
    }
  };

  const removeGalleryItem = (index) => {
    const g = formData.gallery.filter((_, i) => i !== index);
    setFormData({ ...formData, gallery: g });
  };

  const handleSubmit = async () => {
    // Prevent submission if categoryId is not selected
    if (!formData.categoryId || formData.categoryId === '') {
      snackbar('انتخاب دسته‌بندی اجباری است');
      return;
    }
    try {
      if (current) {
        const { sendNotification, ...updatePayload } = formData;
        // اطمینان از ارسال subCategoryId حتی اگر رشته خالی باشد
        if (!updatePayload.subCategoryId) updatePayload.subCategoryId = '';
        await welfareService.updateService(current._id, updatePayload);
        successMessage('خدمت به‌روزرسانی شد');
      } else {
        const payload = { ...formData };
        if (!payload.subCategoryId) payload.subCategoryId = '';
        await welfareService.createService(payload);
        successMessage('خدمت ایجاد شد');
      }
      fetchServices();
      handleClose();
    } catch (err) {
      console.error("err", err)
      snackbar('خطا در ذخیره‌سازی');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این خدمت اطمینان دارید؟')) return;
    try {
      await welfareService.deleteService(id);
      successMessage('حذف شد');
      fetchServices();
    } catch (err) {
      snackbar('خطا در حذف');
    }
  };

  const handleResendNotification = async (id) => {
    if (!window.confirm('ارسال مجدد نوتیفیکیشن برای این خدمت انجام شود؟')) return;
    try {
      await api.post(`/welfare/services/${id}/send-notification`);
      successMessage('نوتیفیکیشن ارسال شد');
    } catch (err) {
      console.error(err);
      snackbar(err?.response?.data?.message || 'خطا در ارسال نوتیفیکیشن');
    }
  };

  const getImageUrl = (filename) => {
    if (!filename) return '';
    if (filename.startsWith('http')) return filename;
    return `${process.env.REACT_APP_FILE_BASE_URL}/uploads/documents/${filename}`;
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3, mt: isMobile ? 3 : 5 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between', 
        gap: 2,
        mb: 3, 
        mt: isMobile ? 6 : 5 
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">مدیریت خدمات رفاهی</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
           <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField select label="دسته‌بندی" value={filterCategory} onChange={e => {
                setFilterCategory(e.target.value);
                setFilterSubCategory('');
              }} sx={{ minWidth: 120, flex: 1 }} size="small">
                <MenuItem value="">همه</MenuItem>
                {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.title}</MenuItem>)}
              </TextField>
              {filterCategory && (
                <TextField select label="زیر دسته" value={filterSubCategory} onChange={e => setFilterSubCategory(e.target.value)} sx={{ minWidth: 120, flex: 1 }} size="small">
                  <MenuItem value="">همه</MenuItem>
                  {(categories.find(c => c._id === filterCategory)?.subCategories || []).map(sub => (
                    <MenuItem key={sub.id} value={sub.id}>{sub.title}</MenuItem>
                  ))}
                </TextField>
              )}
           </Box>
           <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RateReview />}
                onClick={() => navigate('/dashboard/ratings')}
                fullWidth={isMobile}
                sx={{ borderRadius: '10px' }}
                size="small"
              >
                نظرات
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpen()}
                fullWidth={isMobile}
                sx={{ borderRadius: '10px' }}
                size="small"
              >
                خدمت جدید
              </Button>
           </Box>
        </Box>
      </Box>

      {isMobile ? (
        <Grid container spacing={2}>
          {services.map((s) => (
            <Grid item xs={12} key={s._id}>
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee', bgcolor: '#fafafa' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                    <Avatar 
                      src={getImageUrl(s.image)} 
                      variant="rounded" 
                      sx={{ width: 80, height: 80, borderRadius: 3 }} 
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: '150px' }}>
                          {s.title}
                        </Typography>
                        {s.isFeatured && <Star sx={{ color: '#ffc107', fontSize: 20 }} />}
                      </Box>
                      
                      <Typography variant="caption" color="primary" sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', px: 1, borderRadius: 1, display: 'inline-block', mt: 0.5 }}>
                        {s.categoryId?.title || 'بدون دسته'}
                      </Typography>

                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                         {s.metadata?.provider && (
                           <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                             <Store sx={{ fontSize: 12 }} /> {s.metadata.provider}
                           </Typography>
                         )}
                         {s.metadata?.location && (
                           <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                             <LocationOn sx={{ fontSize: 12 }} /> {s.metadata.location}
                           </Typography>
                         )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip 
                      label={s.isActive ? 'فعال' : 'غیرفعال'} 
                      size="small" 
                      color={s.isActive ? 'success' : 'default'} 
                      variant="outlined"
                      sx={{ borderRadius: '8px', height: 24 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleOpen(s)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleResendNotification(s._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                        <NotificationsActive fontSize="small" color="info" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(s._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {services.length === 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed' }}>
                <Typography variant="body2" color="textSecondary">خدمتی یافت نشد</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell align="right">تصویر</TableCell>
                <TableCell align="right">عنوان</TableCell>
                <TableCell align="right">دسته</TableCell>
                <TableCell align="right">وضعیت</TableCell>
                {!isTablet && <TableCell align="right">ویژه</TableCell>}
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((s) => (
                <TableRow key={s._id} hover>
                  <TableCell align="right">
                    <Avatar src={getImageUrl(s.image)} variant="rounded" sx={{ width: 50, height: 50, borderRadius: 2 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>{s.title}</TableCell>
                  <TableCell align="right">{s.categoryId?.title || ''}</TableCell>
                  <TableCell align="right">
                    <Chip label={s.isActive ? 'فعال' : 'غیرفعال'} size="small" color={s.isActive ? 'success' : 'default'} />
                  </TableCell>
                  {!isTablet && (
                    <TableCell align="right">
                      {s.isFeatured ? <Star color="warning" /> : '-'}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpen(s)} title="ویرایش">
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleResendNotification(s._id)} title="ارسال نوتیفیکیشن">
                        <NotificationsActive fontSize="small" color="info" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(s._id)} title="حذف">
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth 
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 4 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {current ? 'ویرایش خدمت رفاهی' : 'افزودن خدمت جدید'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="عنوان خدمت" 
                name="title" 
                value={formData.title} 
                onChange={handleChange} 
                sx={textFieldStyle} 
                required
              />
            </Grid>

            {/* Content & Metadata */}
            <Grid item xs={12} sm={8}>
              <AIRewriteTextField
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                label="توضیحات کامل"
                rows={isMobile ? 10 : 8}
                textFieldProps={{ sx: textFieldStyle }}
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="دسته اصلی" name="categoryId" value={formData.categoryId} onChange={e => {
                    handleChange(e);
                    setFormData(f => ({ ...f, subCategoryId: '' }));
                  }} sx={textFieldStyle}>
                    {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.title}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {formData.categoryId && (
                    <TextField select fullWidth label="زیر دسته" name="subCategoryId" value={formData.subCategoryId} onChange={handleChange} sx={textFieldStyle}>
                      {(categories.find(c => c._id === formData.categoryId)?.subCategories || []).map(sub => (
                        <MuiMenuItem key={sub.id} value={sub.id}>{sub.title}</MuiMenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Main Image Upload */}
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed', borderRadius: 3, bgcolor: '#fcfcfc' }}>
                {formData.image ? (
                  <Box sx={{ position: 'relative', mb: 1 }}>
                    <img src={getImageUrl(formData.image)} alt="Main" style={{ width: '100%', maxHeight: 150, objectFit: 'cover', borderRadius: 8 }} />
                    <IconButton 
                      size="small" 
                      sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'white', border: '1px solid #eee' }}
                      onClick={() => setFormData({ ...formData, image: '' })}
                    >
                      <Close fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ py: 3 }}>
                    <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="caption" display="block">تصویر اصلی</Typography>
                  </Box>
                )}
                <Button variant="contained" component="label" size="small" sx={{ borderRadius: '8px' }}>
                  انتخاب تصویر
                  <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'image')} />
                </Button>
              </Paper>
            </Grid>

            {/* Additional Fields */}
            <Grid item xs={12} sm={4}>
               <TextField fullWidth label="ارائه‌دهنده" value={formData.metadata.provider} onChange={(e) => handleMetadataChange('provider', e.target.value)} sx={textFieldStyle} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="مکان (شهر/منطقه)" value={formData.metadata.location} onChange={(e) => handleMetadataChange('location', e.target.value)} sx={textFieldStyle} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="تاریخ اعتبار/پایان"
                value={formData.metadata.durationJalali || ''}
                onChange={e => handleMetadataChange('durationJalali', e.target.value)}
                placeholder="۱۴۰۳/۱۲/۲۹"
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} sm={9}>
              <TextField fullWidth label="لینک اختصاصی (خارجی)" name="link" value={formData.link} onChange={handleChange} sx={textFieldStyle} placeholder="https://..." />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth type="number" label="ترتیب نمایش" name="order" value={formData.order} onChange={handleChange} sx={textFieldStyle} />
            </Grid>

            {/* Settings Switches */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, bgcolor: '#f5f5f5', p: 1, borderRadius: 2 }}>
                <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="فعال" />
                <FormControlLabel control={<Switch checked={formData.isFeatured} onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })} />} label="ویژه (نمایش در برگزیده‌ها)" />
                <FormControlLabel control={<Switch checked={formData.sendNotification} onChange={(e) => setFormData({ ...formData, sendNotification: e.target.checked })} disabled={!!current} />} label="ارسال نوتیفیکیشن" />
              </Box>
            </Grid>

            {/* Gallery */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, mt: 1 }}>گالری تصاویر (جزئیات بیشتر)</Typography>
              <Grid container spacing={1}>
                {formData.gallery.map((g, idx) => (
                  <Grid item key={idx} xs={4} sm={2}>
                    <Box sx={{ position: 'relative' }}>
                      <img src={getImageUrl(g)} alt="" style={{ width: '100%', height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
                      <IconButton 
                        size="small" 
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white', border: '1px solid #eee', boxShadow: 1 }} 
                        onClick={() => removeGalleryItem(idx)}
                      >
                        <Close sx={{ fontSize: 14 }} color="error" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={4} sm={2}>
                  <Button 
                    variant="outlined" 
                    component="label" 
                    sx={{ width: '100%', height: 70, borderRadius: 2, borderStyle: 'dashed' }}
                  >
                    <Add />
                    <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'gallery')} />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} sx={{ borderRadius: '8px' }}>لغو</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: '8px', px: 4 }}>ذخیره تغییرات</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
  

export default WelfareServicesManagement;
